<template>
  <b-modal
    :visible="visible"
    size="lg"
    :title="testPack ? 'Edit Test Pack' : 'Create Test Pack'"
    :ok-title="testPack ? 'Update' : 'Create'"
    :ok-disabled="isLoading"
    @ok="handleSubmit"
    @cancel="$emit('close')"
    @close="$emit('close')"
  >
    <validation-observer v-slot="{ handleSubmit: validationHandleSubmit }" ref="formObserver">
      <b-form @submit.prevent="validationHandleSubmit(submitForm)">

        <!-- Basic Information -->
        <div class="mb-4">
          <h5 class="mb-3">
            <feather-icon icon="InfoIcon" class="mr-1" size="18"/>
            Basic Information
          </h5>

          <b-row>
            <b-col md="12">
              <validation-provider
                v-slot="validationContext"
                name="Test Pack Name"
                rules="required"
              >
                <b-form-group label="Test Pack Name" label-for="name">
                  <b-form-input
                    id="name"
                    name="name"
                    v-model="form.name"
                    placeholder="Enter test pack name..."
                    :state="getValidationState(validationContext)"
                  />
                  <b-form-invalid-feedback>
                    {{ validationContext.errors[0] }}
                  </b-form-invalid-feedback>
                </b-form-group>
              </validation-provider>
            </b-col>

            <b-col md="12">
              <b-form-group label="Description" label-for="description">
                <b-form-textarea
                  id="description"
                  v-model="form.description"
                  placeholder="Enter test pack description..."
                  rows="3"
                />
              </b-form-group>
            </b-col>

            <b-col md="6">
              <validation-provider
                v-slot="validationContext"
                name="Subject"
                :rules="{ required: true }"
              >
                <b-form-group label="Subject" label-for="subject">
                  <v-select
                    v-model="form.subject_id"
                    :options="subjects"
                    label="name"
                    :reduce="subject => subject.id"
                    placeholder="Select subject..."
                    :state="getValidationState(validationContext)"
                  />
                  <b-form-invalid-feedback>
                    {{ validationContext.errors[0] }}
                  </b-form-invalid-feedback>
                </b-form-group>
              </validation-provider>
            </b-col>

            <b-col md="6">
              <validation-provider
                v-slot="validationContext"
                name="Difficulty Level"
                :rules="{ required: true }"
              >
                <b-form-group label="Difficulty Level" label-for="difficulty">
                  <v-select
                    v-model="form.difficulty_level"
                    :options="difficultyOptions"
                    label="label"
                    :reduce="option => option.value"
                    placeholder="Select difficulty..."
                    :state="getValidationState(validationContext)"
                  />
                  <b-form-invalid-feedback>
                    {{ validationContext.errors[0] }}
                  </b-form-invalid-feedback>
                </b-form-group>
              </validation-provider>
            </b-col>
          </b-row>
        </div>

        <!-- Test Settings -->
        <div class="mb-4">
          <h5 class="mb-3">
            <feather-icon icon="SettingsIcon" class="mr-1" size="18"/>
            Test Settings
          </h5>

          <b-row>
            <b-col md="6">
              <b-form-group label="Time Limit (minutes)" label-for="timeLimit">
                <b-form-input
                  id="timeLimit"
                  v-model.number="form.time_limit"
                  type="number"
                  min="1"
                  placeholder="No time limit"
                />
                <small class="form-text text-muted">Leave empty for no time limit</small>
              </b-form-group>
            </b-col>

            <b-col md="6">
              <b-form-group label="Passing Score (%)" label-for="passingScore">
                <b-form-input
                  id="passingScore"
                  v-model.number="form.passing_score"
                  type="number"
                  min="0"
                  max="100"
                  placeholder="60"
                />
              </b-form-group>
            </b-col>

            <b-col md="6">
              <b-form-group label="Max Attempts" label-for="maxAttempts">
                <b-form-input
                  id="maxAttempts"
                  v-model.number="form.max_attempts"
                  type="number"
                  min="1"
                  placeholder="1"
                />
              </b-form-group>
            </b-col>

            <b-col md="6">
              <b-form-group label="Status" label-for="status">
                <v-select
                  v-model="form.status"
                  :options="statusOptions"
                  label="label"
                  :reduce="option => option.value"
                  placeholder="Select status..."
                />
              </b-form-group>
            </b-col>
          </b-row>
        </div>

        <!-- Advanced Options -->
        <div class="mb-4">
          <h5 class="mb-3">
            <feather-icon icon="ToggleLeftIcon" class="mr-1" size="18"/>
            Advanced Options
          </h5>

          <b-row>
            <b-col md="12">
              <b-form-group label="Instructions" label-for="instructions">
                <b-form-textarea
                  id="instructions"
                  v-model="form.instructions"
                  placeholder="Enter test instructions for students..."
                  rows="4"
                />
              </b-form-group>
            </b-col>

            <b-col md="12">
              <div class="custom-control-wrapper">
                <b-form-checkbox
                  v-model="form.is_randomized"
                  class="mb-2"
                >
                  Randomize Question Order
                  <small class="d-block text-muted">Questions will appear in random order for each student</small>
                </b-form-checkbox>

                <b-form-checkbox
                  v-model="form.show_results_immediately"
                  class="mb-2"
                >
                  Show Results Immediately
                  <small class="d-block text-muted">Students see their results right after completing the test</small>
                </b-form-checkbox>

                <b-form-checkbox
                  v-model="form.allow_retakes"
                  class="mb-2"
                >
                  Allow Retakes
                  <small class="d-block text-muted">Students can retake the test multiple times</small>
                </b-form-checkbox>
              </div>
            </b-col>
          </b-row>
        </div>

        <!-- Tags -->
        <div class="mb-4">
          <h5 class="mb-3">
            <feather-icon icon="TagIcon" class="mr-1" size="18"/>
            Tags
          </h5>

          <b-form-group label="Tags" label-for="tags">
            <b-form-tags
              v-model="form.tags"
              placeholder="Add tags..."
              separator=" ,;"
              remove-on-delete
              no-outer-focus
              class="mb-2"
            />
            <small class="form-text text-muted">Press Enter, Space, comma, or semicolon to add tags</small>
          </b-form-group>
        </div>

      </b-form>
    </validation-observer>

    <template #modal-footer>
      <b-button variant="outline-secondary" @click="$emit('close')">
        Cancel
      </b-button>
      <b-button
        variant="primary"
        :disabled="isLoading"
        @click="handleSubmit"
      >
        <b-spinner v-if="isLoading" small class="mr-1"/>
        <feather-icon v-else icon="SaveIcon" size="16" class="mr-1"/>
        {{ testPack ? 'Update' : 'Create' }}
      </b-button>
    </template>
  </b-modal>
</template>

<script>
import {
  BModal,
  BForm,
  BFormGroup,
  BFormInput,
  BFormTextarea,
  BFormCheckbox,
  BFormTags,
  BFormInvalidFeedback,
  BRow,
  BCol,
  BButton,
  BSpinner
} from 'bootstrap-vue'
import { ValidationObserver, ValidationProvider } from 'vee-validate'
import vSelect from 'vue-select'
import TestManagementApi from '@/api/testManagement'

export default {
  name: 'TestPackModal',
  components: {
    BModal,
    BForm,
    BFormGroup,
    BFormInput,
    BFormTextarea,
    BFormCheckbox,
    BFormTags,
    BFormInvalidFeedback,
    BRow,
    BCol,
    BButton,
    BSpinner,
    ValidationObserver,
    ValidationProvider,
    vSelect
  },

  props: {
    visible: {
      type: Boolean,
      default: false
    },
    testPack: {
      type: Object,
      default: null
    },
    subjects: {
      type: Array,
      default: () => []
    }
  },

  data() {
    return {
      isLoading: false,

      form: {
        name: '',
        description: '',
        subject_id: null,
        difficulty_level: 'intermediate',
        time_limit: null,
        total_points: 0,
        instructions: '',
        is_randomized: false,
        show_results_immediately: true,
        allow_retakes: false,
        max_attempts: 1,
        passing_score: 60,
        status: 'active',
        tags: []
      },

      difficultyOptions: [
        { label: 'Beginner', value: 'beginner' },
        { label: 'Intermediate', value: 'intermediate' },
        { label: 'Advanced', value: 'advanced' }
      ],

      statusOptions: [
        { label: 'Active', value: 'active' },
        { label: 'Inactive', value: 'inactive' }
      ]
    }
  },

  watch: {
    testPack: {
      handler(newVal) {
        this.resetForm()
        if (newVal) {
          this.loadTestPackData(newVal)
        }
      },
      immediate: true
    },

    visible(newVal) {
      if (newVal) {
        this.resetForm()
        if (this.testPack) {
          this.loadTestPackData(this.testPack)
        }
        // Force validation reset
        this.$nextTick(() => {
          if (this.$refs.formObserver) {
            this.$refs.formObserver.reset()
          }
        })
      }
    },

    subjects: {
      handler(newVal) {
        // Subjects received - validation should work now
      },
      immediate: true
    }
  },

  methods: {
    getValidationState({ dirty, validated, valid = null }) {
      return dirty || validated ? valid : null
    },

    resetForm() {
      this.form = {
        name: '',
        description: '',
        subject_id: null,
        difficulty_level: 'intermediate',
        time_limit: null,
        total_points: 0,
        instructions: '',
        is_randomized: false,
        show_results_immediately: true,
        allow_retakes: false,
        max_attempts: 1,
        passing_score: 60,
        status: 'active',
        tags: []
      }

      // Reset validation
      this.$nextTick(() => {
        if (this.$refs.formObserver) {
          this.$refs.formObserver.reset()
        }
      })
    },

    loadTestPackData(testPack) {
      this.form = {
        name: testPack.name || '',
        description: testPack.description || '',
        subject_id: testPack.subject_id || null,
        difficulty_level: testPack.difficulty_level || 'intermediate',
        time_limit: testPack.time || null,
        total_points: testPack.total_points || 0,
        instructions: testPack.instructions || '',
        is_randomized: testPack.settings?.is_randomized || false,
        show_results_immediately: testPack.settings?.show_results_immediately !== false,
        allow_retakes: testPack.settings?.allow_retakes || false,
        max_attempts: testPack.settings?.max_attempts || 1,
        passing_score: testPack.settings?.passing_score || 60,
        status: testPack.status || 'active',
        tags: testPack.settings?.tags || []
      }
    },

    handleSubmit() {
      this.$refs.formObserver.validate().then(success => {
        if (success) {
          this.submitForm()
        }
      })
    },

    async submitForm() {
      this.isLoading = true

      try {
        const formData = {
          ...this.form,
          tags: JSON.stringify(this.form.tags)
        }

        const api = new TestManagementApi()
        let result

        if (this.testPack) {
          result = await api.updateTestPack(this.testPack.id, formData)
        } else {
          result = await api.createTestPack(formData)
        }

        this.$toast({
          component: 'ToastificationContent',
          props: {
            title: 'Success',
            icon: 'CheckIcon',
            text: this.testPack
              ? `Test pack "${this.form.name}" updated successfully`
              : `Test pack "${this.form.name}" created successfully`,
            variant: 'success',
          },
        })

        this.$emit('save', result.data)

      } catch (error) {
        console.error('Error saving test pack:', error)
        this.$toast({
          component: 'ToastificationContent',
          props: {
            title: 'Error',
            icon: 'AlertTriangleIcon',
            text: error.response?.data?.error || 'Failed to save test pack',
            variant: 'danger',
          },
        })
      } finally {
        this.isLoading = false
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.custom-control-wrapper {
  .form-check {
    margin-bottom: 0.75rem;

    .form-check-label {
      font-weight: 500;

      small {
        font-weight: 400;
        margin-top: 0.25rem;
      }
    }
  }
}

::v-deep .modal-header {
  background: linear-gradient(118deg, #7367F0, rgba(115, 103, 240, 0.7));
  color: white;
  border-bottom: none;

  .modal-title {
    color: white !important;
  }

  .close {
    color: white;
    opacity: 0.8;

    &:hover {
      opacity: 1;
    }
  }
}

::v-deep .v-select {
  .vs__dropdown-toggle {
    border: 1px solid #d8d6de;
    border-radius: 0.357rem;
  }

  &.vs--open .vs__dropdown-toggle {
    border-color: #7367F0;
  }
}

h5 {
  color: #2c2c2c;
  font-weight: 600;
  display: flex;
  align-items: center;

  svg {
    color: #7367F0;
  }
}
</style>